import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, Minimize2, Mail, CheckCircle2, UserRound } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const GREETING = {
  role: 'assistant',
  content: "Hi there! I'm GOPIKO's AI assistant. I can help you with posting ads, finding listings, billing, safety tips, auctions, Product Reels, and more. What can I help you with?",
};

const QUICK_PROMPTS = [
  'How do I post an ad?',
  'Is it safe to buy here?',
  'What are your pricing plans?',
  'How do auctions work?',
];

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('gopiko_chat_session') || '');
  const [unread, setUnread] = useState(0);
  const [showEscalation, setShowEscalation] = useState(false);
  const [escalationForm, setEscalationForm] = useState({ email: '', summary: '' });
  const [escalating, setEscalating] = useState(false);
  const [ticketCreated, setTicketCreated] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();

  // Hide on pages where the fab interferes with bottom UI
  const hiddenPaths = ['/reels'];
  const isHidden = hiddenPaths.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, showEscalation, ticketCreated]);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;

    const userMsg = { role: 'user', content: trimmed };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const history = messages.filter(m => m.role !== 'greeting').map(m => ({ role: m.role, content: m.content }));

      const res = await fetch(`${API_URL}/api/support-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: trimmed, session_id: sessionId, history }),
      });

      if (!res.ok) throw new Error('Failed');

      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);

      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem('gopiko_chat_session', data.session_id);
      }

      // Auto-show escalation form if AI flagged it
      if (data.escalate) {
        setShowEscalation(true);
      }

      if (!open) setUnread(prev => prev + 1);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I'm having trouble connecting. Please try again or email support@gopiko.com." }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, sessionId, open]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleNewChat = () => {
    setSessionId('');
    localStorage.removeItem('gopiko_chat_session');
    setMessages([GREETING]);
    setShowEscalation(false);
    setTicketCreated(null);
    setEscalationForm({ email: '', summary: '' });
  };

  const handleEscalate = async (e) => {
    e.preventDefault();
    if (!escalationForm.email.trim() || !escalationForm.summary.trim()) return;
    setEscalating(true);

    try {
      const transcript = messages.map(m => ({ role: m.role, content: m.content }));
      const res = await fetch(`${API_URL}/api/support-chat/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          email: escalationForm.email.trim(),
          summary: escalationForm.summary.trim(),
          transcript,
        }),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setTicketCreated(data);
      setShowEscalation(false);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I couldn't create a ticket right now. Please email support@gopiko.com directly." }]);
      setShowEscalation(false);
    } finally {
      setEscalating(false);
    }
  };

  const msgCount = messages.filter(m => m.role === 'user').length;

  if (isHidden) return null;

  return (
    <>
      {/* Chat Window */}
      {open && (
        <div
          className="fixed bottom-36 md:bottom-20 right-4 sm:right-6 z-[9999] w-[calc(100vw-32px)] sm:w-[380px] max-h-[520px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          style={{ animation: 'chatSlideUp 0.25s ease-out' }}
          data-testid="chat-widget-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">GOPIKO Support</p>
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-gray-400 text-[10px]">AI assistant — always online</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={handleNewChat} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" title="New chat" data-testid="chat-new-btn">
                <Minimize2 className="w-3.5 h-3.5 text-gray-400" />
              </button>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="chat-close-btn">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50" style={{ minHeight: '240px', maxHeight: '320px' }} data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex items-end gap-1.5 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mb-0.5">
                      <Bot className="w-3 h-3 text-red-600" />
                    </div>
                  )}
                  <div
                    className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-red-600 text-white rounded-br-md'
                        : 'bg-white text-gray-800 border border-gray-150 shadow-sm rounded-bl-md'
                    }`}
                    data-testid={`chat-msg-${i}`}
                  >
                    {msg.content}
                  </div>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="flex items-end gap-1.5">
                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mb-0.5">
                    <Bot className="w-3 h-3 text-red-600" />
                  </div>
                  <div className="bg-white border border-gray-150 shadow-sm rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex items-center gap-1" data-testid="chat-typing">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Ticket Created Confirmation */}
            {ticketCreated && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center" data-testid="ticket-confirmation">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-emerald-800 mb-1">Ticket Created</p>
                <p className="text-xs text-emerald-700 font-mono mb-1">{ticketCreated.ticket_id}</p>
                <p className="text-xs text-emerald-600">{ticketCreated.message}</p>
              </div>
            )}

            {/* Inline Escalation Form */}
            {showEscalation && !ticketCreated && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="escalation-form" style={{ animation: 'chatSlideUp 0.2s ease-out' }}>
                <div className="flex items-center gap-2 mb-3">
                  <UserRound className="w-4 h-4 text-amber-700" />
                  <p className="text-sm font-semibold text-amber-900">Connect with our team</p>
                </div>
                <p className="text-xs text-amber-700 mb-3">Leave your email and a brief description. We'll get back to you within 24 hours with your full chat transcript.</p>
                <form onSubmit={handleEscalate} className="space-y-2">
                  <input
                    type="email"
                    required
                    value={escalationForm.email}
                    onChange={(e) => setEscalationForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Your email address"
                    className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 outline-none"
                    data-testid="escalation-email"
                  />
                  <textarea
                    required
                    value={escalationForm.summary}
                    onChange={(e) => setEscalationForm(prev => ({ ...prev, summary: e.target.value }))}
                    placeholder="Briefly describe your issue..."
                    rows={2}
                    className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 outline-none resize-none"
                    data-testid="escalation-summary"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={escalating}
                      className="flex-1 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white text-xs font-bold py-2 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                      data-testid="escalation-submit"
                    >
                      {escalating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                      {escalating ? 'Creating...' : 'Create Ticket'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowEscalation(false)}
                      className="px-3 py-2 text-xs text-amber-700 hover:bg-amber-100 rounded-lg transition-colors font-medium"
                      data-testid="escalation-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Prompts — show only at start */}
          {messages.length <= 1 && !loading && (
            <div className="px-4 py-2 border-t border-gray-100 bg-white flex-shrink-0">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Quick questions</p>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_PROMPTS.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    className="text-xs bg-gray-100 hover:bg-red-50 hover:text-red-700 text-gray-700 px-3 py-1.5 rounded-full border border-gray-200 hover:border-red-200 transition-colors font-medium"
                    data-testid={`chat-quick-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input + Escalation trigger */}
          <div className="border-t border-gray-200 bg-white flex-shrink-0">
            {/* Talk to human link — show after 2+ user messages */}
            {msgCount >= 2 && !showEscalation && !ticketCreated && (
              <div className="px-4 pt-2 pb-0">
                <button
                  onClick={() => setShowEscalation(true)}
                  className="text-[11px] text-gray-400 hover:text-red-600 transition-colors flex items-center gap-1"
                  data-testid="talk-to-human-btn"
                >
                  <UserRound className="w-3 h-3" />
                  Need more help? Talk to a human
                </button>
              </div>
            )}
            <div className="px-3 py-2.5 flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 transition-all"
                disabled={loading}
                maxLength={2000}
                data-testid="chat-input"
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                className="w-9 h-9 rounded-xl bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center transition-colors flex-shrink-0"
                data-testid="chat-send-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Send className="w-4 h-4 text-white" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Bubble */}
      <button
        onClick={() => setOpen(prev => !prev)}
        className="fixed bottom-20 md:bottom-4 right-4 sm:right-6 z-[9999] w-14 h-14 rounded-full bg-orange-500 hover:bg-orange-600 shadow-xl shadow-orange-500/30 hover:shadow-orange-500/40 flex items-center justify-center transition-all hover:scale-105 active:scale-95 group"
        data-testid="chat-widget-bubble"
        aria-label="Open support chat"
      >
        {open ? (
          <X className="w-6 h-6 text-white transition-transform" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6 text-white" />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                {unread}
              </span>
            )}
          </>
        )}
      </button>

      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
};

export default ChatWidget;
